import { Button } from 'blaze-ui/Button'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useInitiateOauth } from 'hooks/useInitiateOauth'
import { useOpenIntegrations } from 'hooks/useOpenIntegrations'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { INTEGRATION_CONFIGS } from 'components/IntegrationsBanner/integrationConfig'
import { AlertTriangle } from 'blaze-ui/icons/20/AlertTriangle'
import cx from 'classnames'
import Styles from './IntegrationReauthBanner.module.scss'
import { AnnotationInformation, ArrowRefresh2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useOutdatedScopeTikTokIntegration } from 'hooks/useOutdatedScopeTikTokIntegration'
import { useGoogleDriveIntegration } from 'hooks/useGoogleDriveIntegration'
import { useRefreshIntegrationConnection } from 'hooks/useRefreshIntegrationConnection'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { AlertTriangle2 } from 'blaze-ui/icons/24'

interface IntegrationReauthBannerProps {
  className?: string
  hideButton?: boolean
  context?: 'design' | 'default' // Context for customizing messages
}

export const IntegrationReauthBanner = ({
  className,
  hideButton = false,
  context = 'default',
}: IntegrationReauthBannerProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const isReauthBannerEnabled = useFlipperFlag('external_integrations_heartbeat')
  const { disconnectedIntegrations, hasDisconnectedIntegrations } = useIntegrationHeartbeatStatus()
  const { outdatedScopeTikTokIntegration } = useOutdatedScopeTikTokIntegration()
  const { needsReauth: googleDriveNeedsReauth, integration: googleDriveIntegration } = useGoogleDriveIntegration()
  const { initiateOauth } = useInitiateOauth()
  const refreshIntegrationConnection = useRefreshIntegrationConnection()
  const openIntegrations = useOpenIntegrations()
  const { isMobile } = useResponsiveDesign()

  if (
    !isReauthBannerEnabled ||
    isMobile ||
    (!hasDisconnectedIntegrations && !outdatedScopeTikTokIntegration && !googleDriveNeedsReauth)
  ) {
    return null
  }

  const handleRefreshConnection = () => {
    if (googleDriveNeedsReauth && !disconnectedIntegrations.length) {
      refreshIntegrationConnection(ExternalIntegrationEnum.GoogleDrive, googleDriveIntegration?.id)
    } else if (disconnectedIntegrations.length === 1) {
      initiateOauth(disconnectedIntegrations[0].provider)
    } else {
      openIntegrations()
    }
  }

  const getProviderName = (integration: (typeof disconnectedIntegrations)[0]) => {
    const configEntry = Object.values(INTEGRATION_CONFIGS).find((config) => config.provider === integration.provider)
    return (
      configEntry?.displayName ||
      integration.provider.charAt(0).toUpperCase() + integration.provider.slice(1).replace('_', ' ')
    )
  }

  const isNotification = outdatedScopeTikTokIntegration && !disconnectedIntegrations.length && !googleDriveNeedsReauth

  const getMessage = () => {
    const hasSingleIntegration = disconnectedIntegrations.length === 1

    if (hasSingleIntegration) {
      const providerName = getProviderName(disconnectedIntegrations[0])
      return `Your ${providerName} connection expired. Publishing will fail unless you refresh your connection.`
    }

    if (disconnectedIntegrations.length > 1) {
      // Multiple disconnected integrations - list them all
      const providerNames = disconnectedIntegrations.map((integration) => getProviderName(integration))
      const formattedList =
        providerNames.length === 2
          ? providerNames.join(' and ')
          : `${providerNames.slice(0, -1).join(', ')}, and ${providerNames[providerNames.length - 1]}`

      return `Your ${formattedList} connections have expired. Publishing will fail unless you refresh your connections.`
    }

    if (googleDriveNeedsReauth) {
      return `Your Google Drive connection needs updated permissions. Please reconnect to continue syncing files.`
    }

    if (outdatedScopeTikTokIntegration) {
      return (
        <div className={Styles.notificationMessage}>
          <Text variant="smallList" color="var(--dark-80)">
            TikTok analytics is now available!
          </Text>
          <Text variant="secondary" color="var(--dark-60)">
            To start collecting metrics, please refresh your TikTok connection.
          </Text>
        </div>
      )
    }
  }

  const alertIcon = isSystemRedesignEnabled ? (
    <AlertTriangle2 size={20} color="var(--red-50)" />
  ) : (
    <AlertTriangle size={20} color="var(--negative-60)" />
  )

  return (
    <div className={cx(Styles.banner, className, { [Styles.isNotification]: isNotification })}>
      <div className={Styles.iconSection}>
        {isNotification ? <AnnotationInformation size={20} color="#000000" /> : alertIcon}
      </div>
      <div className={Styles.content}>
        <span className={Styles.message}>{getMessage()}</span>
      </div>
      {!hideButton && (
        <Button
          variant="tertiary"
          size="sm"
          onPress={handleRefreshConnection}
          className={Styles.refreshButton}
          frontIcon={ArrowRefresh2}
          data-testid="reauth-refresh-button"
        >
          Refresh Connection
        </Button>
      )}
    </div>
  )
}
